import React, { useState, useEffect } from 'react'
import { Form, Col } from 'react-bootstrap'
import { connect } from 'formik'
import * as Yup from 'yup'
import { useTranslation } from 'next-i18next'

import { getAddressConfigByCountry } from '../../utils/generalAPIs'

import allCountriesList from '../../utils/countriesList.json'
import AddressLine1Control from './AddressLine1Control'
import AddressLine2Control from './AddressLine2Control'
import AddressCityControl from './CityControl'
import AddressAdminAreaControl from './AdminAreaControl'
import AddressPostalCodeControl from './PostalCodeControl'

let countriesList = []
const AddressField = ({
  formik,
  formSchema,
  setFormSchema,
  setFieldValue,
  supportedCountries,
  supportedLocationsUrl,
  productTitle,
  canEdit,
  canEditCountry,
  canEditProvince,
  canEditZipcode,
  alignCenter = true,
  columnSize = 5
}) => {
  const { t } = useTranslation('common')
  const [addressFieldConfig, setAddressFieldConfig] = useState({})
  countriesList =
    supportedCountries && supportedCountries.length > 0 ? supportedCountries : allCountriesList

  useEffect(() => {
    const selectAddressFieldsForCountry = async () => {
      if (formik.values.country) {
        const response = await getAddressConfigByCountry(formik.values.country)
        if (response.data) {
          setAddressFieldConfig(response.data)

          // Reset province only if user canEditCountry && canEditProvince
          // To prevent province/state from being set to previously selected country's (dirty data)
          // if (
          //   canEditCountry &&
          //   canEditProvince &&
          //   response.data.province &&
          //   typeof setFieldValue === 'function'
          // ) {
          //   setFieldValue('province', '')
          // }
        }
      }
    }
    selectAddressFieldsForCountry()
  }, [formik.values.country])

  useEffect(() => {
    if (Object.keys(addressFieldConfig).length > 0) {
      const requiredSchema = Yup.string().required('Required')
      const optionalSchema = Yup.string().optional()

      const addressSchema = {
        country: requiredSchema
      }

      for (const property in addressFieldConfig) {
        addressSchema[property] = addressFieldConfig[property].required
          ? requiredSchema
          : optionalSchema
      }

      setFormSchema(formSchema.concat(Yup.object(addressSchema)))
    }
  }, [addressFieldConfig])

  // We need to re-order according to weight
  const OrderedSubFields = []

  // Since this call is client side, server render will have no results yet.
  if (Object.keys(addressFieldConfig).length > 0) {
    OrderedSubFields.push({
      weight: addressFieldConfig.street1.weight,
      element: (
        <AddressLine1Control
          fieldConfig={addressFieldConfig.street1}
          canEdit={canEdit}
          alignCenter={alignCenter}
          columnSize={columnSize}
        />
      )
    })
    OrderedSubFields.push({
      weight: addressFieldConfig.street2.weight,
      element: (
        <AddressLine2Control
          fieldConfig={addressFieldConfig.street2}
          canEdit={canEdit}
          alignCenter={alignCenter}
          columnSize={columnSize}
        />
      )
    })

    OrderedSubFields.push({
      weight: addressFieldConfig.city.weight,
      element: (
        <AddressCityControl
          fieldConfig={addressFieldConfig.city}
          canEdit={canEdit}
          alignCenter={alignCenter}
          columnSize={columnSize}
        />
      )
    })

    // NOTE: states and provinces both count as an province
    OrderedSubFields.push({
      weight: addressFieldConfig.province.weight,
      element: (
        <AddressAdminAreaControl
          fieldConfig={addressFieldConfig.province}
          canEdit={canEditProvince}
          alignCenter={alignCenter}
          columnSize={columnSize}
        />
      )
    })

    OrderedSubFields.push({
      weight: addressFieldConfig.zipCode.weight,
      element: (
        <AddressPostalCodeControl
          fieldConfig={addressFieldConfig.zipCode}
          canEdit={canEditZipcode}
          alignCenter={alignCenter}
          columnSize={columnSize}
        />
      )
    })

    // Just a placeholder to properly align last item
    if (OrderedSubFields.length % 2 == 1) {
      OrderedSubFields.push({
        weight: 10,
        element: <Col md={columnSize} className="mx-auto"></Col>
      })
    }

    OrderedSubFields.sort((a, b) => {
      if (a.weight > b.weight) {
        return 1
      }
      return -1
    })
  }

  return (
    <>
      <Col>
        <h6 className="section-name">{t('COMPANY_LOCATION')}</h6>
      </Col>
      <Form.Group
        as={Col}
        md={columnSize}
        className={alignCenter ? 'mx-auto' : ''}
        controlId="formCountry"
      >
        <Form.Label>{t('Location')}</Form.Label>
        <Form.Control
          as="select"
          type="select"
          value={formik.values.country}
          name="country"
          className="custom-select"
          isInvalid={!!formik.errors.country}
          onChange={(e) => {
            // Not needed anymore now that useEffect is run on "formik.values.country" change. Nice.
            // setAddressFieldsForCountry(e.target.value)
            formik.handleChange(e)
            if (canEditCountry && canEditProvince && typeof setFieldValue === 'function') {
              setFieldValue('province', '')
            }
          }}
          //disabled={countryDisabled || disabledField}
          disabled={!canEditCountry}
        >
          {/* NOTE: We will always at least have a default from SA. So we should never need empty option. */}
          {/*<option value="" defaultValue>*/}
          {/*  - Select a country -*/}
          {/*</option>*/}

          {countriesList.map((country, index) => (
            <option key={country.iso2} value={country.iso2}>
              {country.name}
            </option>
          ))}
        </Form.Control>
        <Form.Control.Feedback type="invalid">{formik.errors.country}</Form.Control.Feedback>
        {supportedLocationsUrl && (
          <p className="small mt-1 m-0">
            {t('Dont_see_your_location_text')}&nbsp;
            <a href={supportedLocationsUrl} target="_blank" rel="noreferrer">
              {t('list_of_supported_locations_for')} {productTitle}
            </a>
          </p>
        )}
      </Form.Group>
      {OrderedSubFields.map((item, index) => (
        <React.Fragment key={`address-field-${index}`}>{item.element}</React.Fragment>
      ))}
    </>
  )
}

export default connect(AddressField)
